import React, { useEffect, useState } from "react";
import { getPageBySlug } from "../lib/cms";
import { 
  Car, 
  Award, 
  ShieldCheck, 
  Heart, 
  ChevronDown, 
  Sparkles, 
  Users, 
  CheckCircle2, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  Compass, 
  Zap, 
  Gauge, 
  ShieldAlert 
} from "lucide-react";
import { Link } from "react-router-dom";

export default function OmCarina() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCarTab, setActiveCarTab] = useState("audi");
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("om-carina");
        setPageData(page);
      } catch (err) {
        console.error("Error loading page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title || "Bilen & Kørelæreren | Carinas Køreskole";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    } else {
      document.title = "Bilen & Kørelæreren | Carinas Køreskole Gug";
    }
  }, [pageData]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
          <div className="text-brand-primary font-bold text-lg">Henter personlig profil...</div>
        </div>
      </div>
    );
  }

  // Car specifications data
  const carSpecs = {
    audi: {
      name: "Audi Q3 Sportback",
      gear: "Manuelt gear (6-trins)",
      engine: "2.0 TDI (150 HK)",
      fuel: "Diesel (Miljøvenlig Euro 6)",
      safety: "5/5 stjerner i Euro NCAP",
      color: "Gletsjerhvid Metallak",
      features: [
        "Høj siddeposition for sublimt udsyn og tryghed",
        "Virtuelt cockpit med tydelig navigation direkte foran dig",
        "Parkeringssensorer og bakkamera til nem manøvrering",
        "Vognbaneassistent og automatisk nødbremse",
        "Ergonomiske sportssæder med lændestøtte"
      ],
      description: "Audi Q3 er vores populære skolevogn til manuelt gear. Det er en mellemstor SUV, hvilket betyder, at du sidder dejligt højt hævet over vejen. Det giver et fantastisk og beroligende udsyn over trafikken, hvilket især nye elever er utroligt glade for. Den er utrolig nem at betjene, har en blød kobling og ligger klippestabilt på vejen.",
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-321b9f7c775a4e86a5b9f48c2a0bac79-mv2.jpg",
      interiorImage: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-ec2935c9b5ce4047ad607d07d78978f3-mv2.jpg"
    },
    vw: {
      name: "Volkswagen ID.3 Pro",
      gear: "Automatgear (Trinløst)",
      engine: "100% Elektrisk (204 HK)",
      fuel: "Grøn El (0 g/km CO2 under kørsel)",
      safety: "5/5 stjerner i Euro NCAP",
      color: "Stonewashed Silver Metallic",
      features: [
        "Lydløs og fuldstændig vibrationsfri kørsel",
        "Intuitivt touch-display og head-up display",
        "Avanceret bakkamera med 360-graders visning",
        "Adaptiv fartpilot og Travel Assist",
        "Kompakt ydre, men enorm rummelighed indeni"
      ],
      description: "VW ID.3 er vores topmoderne elbil til automatgear. Da det er en elbil, er der ingen kobling eller gearstang – du skal blot koncentrere dig om at styre, accelerere og bremse. Den kører fuldstændig lydløst og accelererer helt jævnt, hvilket fjerner en stor del af stressen ved at lære at køre bil. Den er perfekt til dig, der ønsker en moderne og grøn køreoplevelse.",
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/id-3-2.jpeg",
      interiorImage: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/id3-1.jpeg"
    }
  };

  const currentCar = carSpecs[activeCarTab];

  const values = [
    {
      icon: <Heart className="h-6 w-6 text-brand-primary" />,
      title: "Tålmodighed i højsædet",
      desc: "Jeg hæver aldrig stemmen. Hos mig er der plads til at lave fejl, og vi øver tingene lige så mange gange, som du har brug for, indtil du føler dig helt tryg."
    },
    {
      icon: <Users className="h-6 w-6 text-brand-primary" />,
      title: "Små, hyggelige hold (Max 8)",
      desc: "Teoriundervisningen foregår i små hold i Gug. Det sikrer en uformel atmosfære med plads til dialog, personlig hjælp og altid kolde sodavand og snacks."
    },
    {
      icon: <CheckCircle2 className="h-6 w-6 text-brand-primary" />,
      title: "Fuld gennemsigtighed",
      desc: "Ingen ubehagelige overraskelser eller skjulte gebyrer. Alle priser og eksterne udgifter lister vi ærligt op, så du præcis ved, hvad kørekortet koster."
    },
    {
      icon: <Sparkles className="h-6 w-6 text-brand-primary" />,
      title: "Moderne udstyr & biler",
      desc: "Du lærer at køre i sikre, komfortable biler udstyret med den nyeste teknologi, hvilket gør kørelektionerne både sjovere, nemmere og mere sikre."
    }
  ];

  const timeline = [
    {
      year: "Nov 2018",
      title: "Uddannet Kørelærer",
      desc: "Jeg afsluttede min uddannelse som kørelærer med en dyb passion for pædagogisk formidling og trafiksikkerhed."
    },
    {
      year: "2018 - 2019",
      title: "Erfaring i Viborg",
      desc: "Arbejdede som fuldtids kørelærer i Viborg, hvor jeg fik værdifuld erfaring med mange forskellige typer elever og køreforhold."
    },
    {
      year: "Aug 2019",
      title: "Egen køreskole i Gug",
      desc: "Drømmen gik i opfyldelse! Jeg slog dørene op til min egen køreskole på Gugvej 186 D for at tilbyde en mere personlig og nærværende køreuddannelse."
    },
    {
      year: "I dag",
      title: "Over 100+ glade bilister",
      desc: "Køreskolen er i dag kendt for sin tålmodige tilgang, høje beståelsesprocent og hyggelige rammer i Gug."
    }
  ];

  const faqs = [
    {
      q: "Må jeg køre bil med manuelt gear, hvis jeg tager kørekort i elbilen (automatgear)?",
      a: "Fra 1. juli 2024 er reglerne blevet markant lempeligere! Hvis du tager kørekortet i en bil med automatgear (f.eks. vores VW ID.3), kan du efterfølgende få fjernet koden i dit kørekort (så du også må køre manuelt gear) blot ved at tage mindst 7 kørelektioner á 45 minutter på vej i en manuelt gearet bil hos en køreskole. Du skal altså IKKE op til en ny praktisk prøve."
    },
    {
      q: "Hvad er fordelene ved at vælge automatgear (elbil)?",
      a: "Fordelene er enorme, især for nye bilister! Du slipper for at tænke på koblingspunkt, gearskift og risikoen for at gå i stå i et lyskryds eller på en bakke. Det frigiver utroligt meget mental energi, så du kan fokusere 100% på trafikken, svingene og orienteringen. Mange elever oplever, at de føler sig hurtigere trygge og klar til prøven i en automatgear-bil."
    },
    {
      q: "Kan jeg skifte mellem manuelt og automatgear undervejs i forløbet?",
      a: "Ja, det kan vi godt arrangere! Da vi har begge biler til rådighed, har du en unik fleksibilitet. Det er dog bedst at beslutte sig, inden vi starter kørslen på vej (efter manøvrebanen), så du får mest muligt kontinuitet i din køreundervisning."
    },
    {
      q: "Hvorfor er bilerne så forskellige (Audi SUV vs. VW Elbil)?",
      a: "Jeg har bevidst valgt to forskellige biltyper for at kunne tilbyde det absolut bedste til begge behov. Audi Q3 SUV'en giver dig en høj, tryg siddeposition med fantastisk udsyn, hvilket er ideelt, når du skal lære at mestre kobling og gear. VW ID.3 elbilen repræsenterer fremtiden – den er kompakt, lydløs, ekstremt nem at manøvrere og perfekt til en stressfri køreoplevelse med automatgear."
    }
  ];

  return (
    <div className="bg-brand-light min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-radial-[at_top_right] from-brand-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> Mød din kørelærer
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-brand-dark tracking-tight leading-none">
                Tryg køreundervisning & <span className="text-brand-primary">moderne biler</span> i Gug
              </h1>
              <p className="text-stone-600 text-lg sm:text-xl leading-relaxed max-w-2xl">
                Hos Carinas Køreskole handler det om dig. Jeg skaber et roligt, tålmodigt og hyggeligt rum, hvor du kan lære at køre bil i dit helt eget tempo – helt uden stress eller hævede stemmer.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-stone-200 shadow-xs">
                  <ShieldCheck className="h-5 w-5 text-brand-primary" />
                  <span className="text-stone-700 text-sm font-bold">Godkendt af Færdselsstyrelsen</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-stone-200 shadow-xs">
                  <Award className="h-5 w-5 text-brand-primary" />
                  <span className="text-stone-700 text-sm font-bold">Uddannet siden 2018</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-4 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover shadow-lg transition-all duration-200 text-center text-sm"
                >
                  Tilmeld dig næste holdstart
                </Link>
                <Link
                  to="/priser"
                  className="px-8 py-4 bg-white text-brand-dark font-bold rounded-xl hover:bg-stone-50 border border-stone-200 shadow-sm transition-all duration-200 text-center text-sm"
                >
                  Se priser & beregner
                </Link>
              </div>
            </div>

            {/* Right Image Stack */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[380px] sm:max-w-[420px] lg:max-w-none">
                {/* Decorative background element */}
                <div className="absolute -inset-4 bg-brand-primary/10 rounded-3xl blur-2xl -z-10 transform rotate-3" />
                
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-2xl bg-stone-100 aspect-[4/5] z-10">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/billede-af-mig-selv.jpg"
                    alt="Kørelærer Carina Svensson"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Floating Badge */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-xl border border-stone-100 shadow-lg flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                      <Award className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-brand-dark text-sm leading-tight">Carina Svensson</h4>
                      <p className="text-stone-500 text-xs">Din personlige og tålmodige kørelærer</p>
                    </div>
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 bg-brand-dark text-white p-4 rounded-2xl shadow-xl z-20 border border-white/10 animate-float-slow hidden sm:block">
                  <div className="text-center">
                    <span className="block text-2xl font-black text-brand-primary">100%</span>
                    <span className="text-[10px] uppercase font-bold tracking-wider text-stone-300">Tålmodighedsgaranti</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Interactive Car Section */}
      <section className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider rounded-lg mb-3">
              <Car className="h-3.5 w-3.5" /> Skolevognene
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-dark tracking-tight mb-4">
              Vælg din køreoplevelse
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Vi tilbyder køreundervisning i to fantastiske biler. Uanset om du vil lære at køre med manuelt gear eller foretrækker den nemme og lydløse elbil med automatgear, har vi den perfekte bil til dig.
            </p>

            {/* Interactive Tabs */}
            <div className="flex justify-center gap-4 mt-8 p-1.5 bg-stone-100 rounded-2xl max-w-md mx-auto border border-stone-200">
              <button
                onClick={() => setActiveCarTab("audi")}
                className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                  activeCarTab === "audi"
                    ? "bg-white text-brand-dark shadow-md"
                    : "text-stone-500 hover:text-brand-dark"
                }`}
              >
                <Gauge className="h-4 w-4" /> Manuel (Audi Q3)
              </button>
              <button
                onClick={() => setActiveCarTab("vw")}
                className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                  activeCarTab === "vw"
                    ? "bg-white text-brand-dark shadow-md"
                    : "text-stone-500 hover:text-brand-dark"
                }`}
              >
                <Zap className="h-4 w-4" /> Automat (VW ID.3)
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            
            {/* Left: Car Images */}
            <div className="lg:col-span-6 space-y-6">
              <div className="relative rounded-3xl overflow-hidden border border-stone-200 shadow-xl group">
                <img
                  src={currentCar.image}
                  alt={currentCar.name}
                  className="w-full h-[300px] sm:h-[380px] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <div className="absolute top-4 left-4 bg-brand-dark/90 text-white text-xs font-bold px-3 py-1.5 rounded-lg backdrop-blur-xs">
                  {currentCar.gear}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-md">
                  <img
                    src={currentCar.interiorImage}
                    alt={`${currentCar.name} kabine`}
                    className="w-full h-[140px] sm:h-[180px] object-cover"
                  />
                </div>
                <div className="bg-stone-50 rounded-2xl border border-stone-200/60 p-5 flex flex-col justify-center space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-brand-primary">Sikkerhed i top</span>
                  <p className="text-stone-700 font-bold text-sm">Euro NCAP Rating</p>
                  <div className="flex gap-1 text-amber-500">
                    <i className="fa-solid fa-star text-xs" />
                    <i className="fa-solid fa-star text-xs" />
                    <i className="fa-solid fa-star text-xs" />
                    <i className="fa-solid fa-star text-xs" />
                    <i className="fa-solid fa-star text-xs" />
                  </div>
                  <p className="text-stone-500 text-xs">Udstyret med de nyeste aktive sikkerhedssystemer.</p>
                </div>
              </div>
            </div>

            {/* Right: Car Details */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-primary">{currentCar.gear}</span>
                <h3 className="text-2xl sm:text-3xl font-black text-brand-dark mt-1">{currentCar.name}</h3>
              </div>

              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                {currentCar.description}
              </p>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-4 border-y border-stone-100 py-6">
                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-stone-400">Gearkasse</span>
                  <p className="text-stone-800 font-bold text-sm">{currentCar.gear}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-stone-400">Motor / Ydelse</span>
                  <p className="text-stone-800 font-bold text-sm">{currentCar.engine}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-stone-400">Drivmiddel</span>
                  <p className="text-stone-800 font-bold text-sm">{currentCar.fuel}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-stone-400">Farve</span>
                  <p className="text-stone-800 font-bold text-sm">{currentCar.color}</p>
                </div>
              </div>

              {/* Key Features List */}
              <div className="space-y-3">
                <h4 className="font-bold text-brand-dark text-sm uppercase tracking-wider">Fremhævede funktioner:</h4>
                <ul className="space-y-2.5">
                  {currentCar.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-stone-600 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
              Mine værdier
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-dark tracking-tight mb-4">
              Hvorfor vælge Carinas Køreskole?
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Jeg tror på, at den bedste indlæring sker i et trygt, roligt og positivt miljø. Her er de fire grundpiller, som min køreskole er bygget på:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((val, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 flex gap-5 items-start"
              >
                <div className="p-3.5 bg-brand-primary/10 rounded-2xl shrink-0">
                  {val.icon}
                </div>
                <div className="space-y-2">
                  <h3 className="font-black text-brand-dark text-lg sm:text-xl">{val.title}</h3>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">{val.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
              Min rejse
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-dark tracking-tight mb-4">
              Historien bag køreskolen
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Fra færdiguddannet kørelærer til etableringen af en af Gugs mest personlige køreskoler. Her er min rejse:
            </p>
          </div>

          {/* Timeline Layout */}
          <div className="relative border-l-2 border-stone-200 ml-4 sm:ml-6 lg:ml-32 space-y-12 py-4">
            {timeline.map((item, idx) => (
              <div key={idx} className="relative pl-8 sm:pl-10">
                {/* Timeline dot */}
                <div className="absolute -left-[11px] top-1.5 bg-brand-primary border-4 border-white h-5 w-5 rounded-full shadow-md" />
                
                {/* Year tag for desktop */}
                <div className="lg:absolute lg:-left-36 lg:top-1 lg:w-28 lg:text-right hidden lg:block">
                  <span className="text-brand-primary font-black text-lg">{item.year}</span>
                </div>

                <div className="space-y-2">
                  {/* Year tag for mobile */}
                  <span className="inline-block lg:hidden text-brand-primary font-black text-sm mb-1">{item.year}</span>
                  <h3 className="text-xl font-black text-brand-dark">{item.title}</h3>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed max-w-2xl">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Gear & Cars FAQ Section */}
      <section className="py-16 lg:py-24 bg-brand-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
              Gear & Regler
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-dark tracking-tight mb-4">
              Ofte stillede spørgsmål om bilerne
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Er du i tvivl om, hvorvidt du skal vælge manuelt eller automatgear? Her finder du svar på de mest almindelige spørgsmål om vores biler og de gældende regler.
            </p>
          </div>

          {/* Accordion */}
          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-xs transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left font-bold text-brand-dark hover:text-brand-primary transition-colors duration-200 gap-4"
                  >
                    <span className="text-base sm:text-lg">{faq.q}</span>
                    <ChevronDown className={`h-5 w-5 text-stone-400 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-brand-primary" : ""}`} />
                  </button>
                  
                  {isOpen && (
                    <div className="px-5 pb-5 sm:px-6 sm:pb-6 border-t border-stone-100 pt-4">
                      <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Extra warning banner */}
          <div className="mt-8 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex gap-4 items-start">
            <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-bold text-amber-900 text-sm">Vigtigt vedr. valg af gear</h4>
              <p className="text-amber-800 text-xs leading-relaxed">
                Uanset hvilken bil du vælger, er teoritimerne og teoriprøven præcis den samme. Du behøver derfor ikke at beslutte dig for gear-type, før vi skal i gang med de praktiske kørelektioner på vejen.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Call to Action (CTA) */}
      <section className="py-16 lg:py-24 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-[at_bottom_left] from-brand-primary/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white">
            Klar til at tage plads i førersædet?
          </h2>
          <p className="text-stone-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Tilmeld dig næste holdstart i Gug, eller kontakt mig for en uforpligtende snak om dit forløb. Jeg glæder mig til at hjælpe dig trygt på vej!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
            <Link
              to="/kontakt"
              className="px-8 py-4 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover shadow-lg transition-all duration-200 text-sm"
            >
              Tilmeld dig nu
            </Link>
            <a
              href="tel:30517269"
              className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 border border-white/10 transition-all duration-200 text-sm flex items-center justify-center gap-2"
            >
              <Phone className="h-4 w-4" /> Ring til Carina (30 51 72 69)
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 max-w-3xl mx-auto border-t border-white/10">
            <div className="flex items-center gap-3 justify-center text-stone-300 text-sm">
              <MapPin className="h-5 w-5 text-brand-primary shrink-0" />
              <span>Teori i Gug (Gugvej 186 D)</span>
            </div>
            <div className="flex items-center gap-3 justify-center text-stone-300 text-sm">
              <Phone className="h-5 w-5 text-brand-primary shrink-0" />
              <span>Tlf: 30 51 72 69</span>
            </div>
            <div className="flex items-center gap-3 justify-center text-stone-300 text-sm">
              <Mail className="h-5 w-5 text-brand-primary shrink-0" />
              <span>carina@carinaskoereskole.dk</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
